// src/pages/subscription/SubscriptionUpgradePage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext.js';
import UpgradeSubscriptionForm from '../../components/subscription/UpgradeSubscriptionForm.js';
import { getError } from '../../utils/errorMessages.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Button } from '../../components/ui/button.js';
import { Crown, Shield, Heart, TrendingUp, Star, Users, Check, HelpCircle, Mail, ArrowUpRight } from 'lucide-react';

const SubscriptionUpgradePage = () => {
  const navigate = useNavigate();
  const { subscription, upgradeSubscription } = useSubscription();
  
  // Get current tier or default to basic
  const currentTier = subscription?.tier || 'basic';
  const isElite = currentTier === 'elite';
  
  // Handle successful upgrade
  const handleUpgradeSuccess = () => {
    navigate('/subscription/manage');
  };

  // Handle direct upgrade button click
  const handleUpgradeClick = async (tier) => {
    try {
      // Scroll to the plan card if it exists
      const planElement = document.getElementById(`${tier}-plan`);
      if (planElement) {
        planElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Target tier is passed through (premium or elite) — Checkout vs in-place
      // upgrade is decided in SubscriptionContext from Stripe sub presence.
      const result = await upgradeSubscription(tier);
      
      if (result.success && (result.url || result.sessionId)) {
        if (result.url) {
          window.location.href = result.url;
          return;
        }
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (!stripeKey) {
          console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
          alert('Payment system not configured. Please contact support.');
          return;
        }
        const stripe = await loadStripe(stripeKey);
        if (stripe) {
          const { error: checkoutError } = await stripe.redirectToCheckout({ sessionId: result.sessionId });
          if (checkoutError) {
            alert(checkoutError.message || 'Could not open Stripe Checkout. Please try again.');
          }
        }
      } else if (result.success) {
        alert(result.message || `Successfully upgraded to ${tier}!`);
        navigate('/subscription/manage');
      } else {
        alert(result.error || 'Unable to start checkout. Please try again.');
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert(getError(error, 'Unable to start checkout. Please try again or contact support.'));
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-6">
            <Star className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Unlock Your Full Potential
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Take your fitness journey to the next level with our premium features designed specifically for active seniors.
          </p>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Upgrade Form - Takes 2 columns */}
          <div className="lg:col-span-2">
            <UpgradeSubscriptionForm onSuccess={handleUpgradeSuccess} />
          </div>
          
          {/* Sidebar - Takes 1 column */}
          <div className="space-y-6">
            {/* Why Upgrade Card - Only show for non-Elite users */}
            {!isElite && (
              <Card className="bg-white shadow-lg border-0">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <CardTitle className="text-lg font-semibold">Why Upgrade?</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {currentTier === 'basic' ? (
                    // Basic user - show Premium features
                    <>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Personalized Guidance</h4>
                          <p className="text-sm text-gray-600">Tailored to your fitness level and goals</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Unlimited Tracking</h4>
                          <p className="text-sm text-gray-600">Monitor your progress with detailed analytics</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Advanced Safety</h4>
                          <p className="text-sm text-gray-600">Peace of mind with smart safety features</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Priority Support</h4>
                          <p className="text-sm text-gray-600">Get help when you need it most</p>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-gray-100">
                        <Button 
                          onClick={() => handleUpgradeClick('premium')}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Upgrade to Premium - $19/month
                        </Button>
                      </div>
                    </>
                  ) : (
                    // Premium user - show Elite features
                    <>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Full Guided Workout Videos</h4>
                          <p className="text-sm text-gray-600">Professional trainer-led workout videos</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Family Monitoring</h4>
                          <p className="text-sm text-gray-600">Dashboard for family members to track progress</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Emergency Integration</h4>
                          <p className="text-sm text-gray-600">Emergency contact system integration</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Custom Workout Plans</h4>
                          <p className="text-sm text-gray-600">Tailored plans designed specifically for you</p>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-gray-100">
                        <Button 
                          onClick={() => handleUpgradeClick('elite')}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Elite - $39/month
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Success Stories Card */}
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Heart className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg font-semibold">Success Stories</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-sm italic mb-2">"I've never felt stronger at 68!"</p>
                  <p className="text-xs opacity-90">- Margaret, Premium Member</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-sm italic mb-2">"The safety features give me confidence"</p>
                  <p className="text-xs opacity-90">- Robert, Elite Member</p>
                </div>
                {!isElite && (
                  <div className="pt-4 border-t border-white/20">
                    <Button 
                      onClick={() => handleUpgradeClick('elite')}
                      className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Upgrade to Elite - $39/month
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Need Help Card */}
            <Card className="bg-white shadow-lg border-0">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold">Need Help?</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Not sure which plan is right for you? Our team is here to help you choose the best option for your needs.
                </p>
                <div className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/contact')}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start"
                    onClick={() => navigate('/subscription/plans')}
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    Compare All Plans
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Trust Indicators */}
        <div className="text-center py-8 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="flex flex-col items-center space-y-2">
              <Shield className="w-8 h-8 text-green-500" />
              <h3 className="font-semibold text-gray-900">Secure & Private</h3>
              <p className="text-sm text-gray-600">Your data is protected with bank-level security</p>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Users className="w-8 h-8 text-purple-500" />
              <h3 className="font-semibold text-gray-900">Expert Support</h3>
              <p className="text-sm text-gray-600">Get help from senior fitness specialists</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionUpgradePage;